import React from 'react'
import { cn } from '@/lib/utils'
import { useSound } from '@/stores/useSoundStore'

interface RetroButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'marble' | 'flat-3d'
  isActive?: boolean
  label?: string
}

export const RetroButton = React.forwardRef<
  HTMLButtonElement,
  RetroButtonProps
>(
  (
    {
      className,
      variant = 'default',
      isActive,
      label,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { playClick } = useSound()

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      playClick()
      if (onClick) onClick(e)
    }

    if (variant === 'marble') {
      return (
        <div
          className="flex items-center gap-3 group cursor-pointer"
          onClick={handleClick as any}
        >
          <button
            ref={ref}
            type="button"
            className={cn(
              'w-8 h-8 rounded-full retro-marble-btn transition-transform duration-75 ease-out',
              isActive && 'retro-marble-btn-active',
              className,
            )}
            {...props}
          />
          {label && (
            <span
              className={cn(
                'font-serif text-lg group-hover:text-[#4A4AFF] transition-colors',
                isActive && 'font-bold text-black',
              )}
            >
              {label}
            </span>
          )}
        </div>
      )
    }

    if (variant === 'flat-3d') {
      return (
        <button
          ref={ref}
          onClick={handleClick}
          className={cn(
            'px-4 py-1 font-serif text-black transition-all active:translate-x-[1px] active:translate-y-[1px]',
            isActive
              ? 'retro-bevel-inset bg-gray-200'
              : 'retro-bevel-outset hover:brightness-110',
            className,
          )}
          {...props}
        >
          {children}
        </button>
      )
    }

    // Default rounded 3D button
    return (
      <button
        ref={ref}
        onClick={handleClick}
        className={cn(
          'px-4 py-2 rounded-full font-serif font-bold text-sm uppercase tracking-wide transition-all active:scale-95 shadow-md',
          'bg-gradient-to-b from-gray-100 to-gray-300 border border-gray-400',
          'hover:from-white hover:to-gray-200',
          className,
        )}
        {...props}
      >
        {children}
      </button>
    )
  },
)
RetroButton.displayName = 'RetroButton'
